import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { api, pickLocale, formatApiErrorDetail } from '@/lib/api';

const empty = () => ({ caption: { en: '', hi: '', mr: '', gu: '' }, image_url: '', kind: 'photo', category: 'general' });

export default function AdminGallery() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ['admin-gallery'], queryFn: async () => (await api.get('/gallery')).data });
  const [form, setForm] = useState(empty());

  const createMut = useMutation({
    mutationFn: async (d) => (await api.post('/admin/gallery', d)).data,
    onSuccess: () => { toast.success('Added'); qc.invalidateQueries(['admin-gallery']); qc.invalidateQueries(['gallery']); setForm(empty()); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/gallery/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries(['admin-gallery']); qc.invalidateQueries(['gallery']); },
  });

  return (
    <div data-testid="admin-gallery-page">
      <h1 className="font-serif text-3xl text-ukgm-blue font-semibold">Manage Gallery</h1>

      <div className="mt-8 bg-white rounded-2xl border border-ukgm-gold/25 p-6 space-y-4">
        <div className="font-serif text-lg text-ukgm-blue font-semibold">Add new item</div>
        <input placeholder="Image URL" value={form.image_url} onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))} className="w-full px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" data-testid="gallery-image-url" />
        <input placeholder="Category (e.g. events, gaushala)" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" />
        <div className="grid md:grid-cols-2 gap-3">
          {['en', 'hi', 'mr', 'gu'].map(lg => (
            <input key={lg} placeholder={`Caption ${lg.toUpperCase()}`} value={form.caption[lg]} onChange={e => setForm(f => ({ ...f, caption: { ...f.caption, [lg]: e.target.value } }))} className="px-4 py-2.5 rounded-full bg-ukgm-cream text-sm" />
          ))}
        </div>
        <button onClick={() => createMut.mutate(form)} className="inline-flex items-center gap-2 bg-ukgm-gold text-ukgm-blue font-bold px-5 py-2.5 rounded-full" data-testid="add-gallery-btn"><Plus className="w-4 h-4" /> Add</button>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map(g => (
          <div key={g.id} className="relative rounded-xl overflow-hidden border border-ukgm-gold/20 bg-white group">
            <img src={g.image_url} alt="" className="w-full h-40 object-cover" />
            <div className="p-2 text-xs text-ukgm-slate truncate">{pickLocale(g.caption, 'en')}</div>
            <button onClick={() => window.confirm('Delete?') && deleteMut.mutate(g.id)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
